// @ts-ignore
import { getH5PIntegration } from 'app/shared/h5p/assets/h5p-integration';

import { getAssets } from 'app/shared/h5p/assets/h5p-assets';
import { CourseActivity } from '@models/course/course-activity.model';
import { Injectable } from '@angular/core';

import {
  ScriptInjectorService,
  ScriptStore,
} from '@services/script-injector.service';
import { TIME_UNITS } from '@core/constants/time-units.constants';
import { LanguageService } from '@app/core/services/language.service';
import { FeatureFlagService } from '@app/core/services/feature-flag.service';
import { MemberService } from '@app/core/services/member.service';
import { FEATURES } from '@app/core/constants/features.constant';
import { take } from 'rxjs';
import { CompanySettingService } from '@app/core/services/company-setting.service';

@Injectable({
  providedIn: 'root',
})
export class H5PInitialization {
  private promiseInProgress: Promise<unknown> | undefined;

  // @ts-ignore
  private assets: any;

  // @ts-ignore
  private H5PIntegration: any;
  constructor(
    private scriptService: ScriptInjectorService,
    private languageService: LanguageService,
    private companySettingService: CompanySettingService,
    private featureFlagService: FeatureFlagService,
    private memberService: MemberService,
  ) {}

  initGlobalH5P(): Promise<unknown> {
    const supportedLang = this.languageService.currentLanguage();
    this.assets = getAssets(supportedLang);
    this.H5PIntegration = getH5PIntegration(supportedLang);
    if (this.promiseInProgress) {
      return this.promiseInProgress;
    }

    this.promiseInProgress = new Promise((resolve, reject) => {
      try {
        // @ts-ignore
        if (globalThis.H5PIntegration || globalThis.H5PEditor) {
          // Check if language changed and load correct editor language
          if (this.scriptService.isLoaded(this.assets.currentTranslation)) {
            resolve(true);
          } else {
            this.scriptService.unload(this.assets.allTranslations);
            this.scriptService.load(this.assets.currentTranslation).then(() => {
              resolve(true);
            });
          }
        } else {
          // @ts-ignore
          globalThis.H5PIntegration = this.H5PIntegration;
          const baseUrl = this.H5PIntegration.editor.baseUrl;
          const allScripts = this.assets.js.concat(this.assets.allTranslations);
          const usedScripts = this.assets.js.concat(
            this.assets.currentTranslation,
          );
          ScriptStore.push(
            ...allScripts.map((asset: string) => ({
              name: asset,
              src: baseUrl + '/' + asset,
            })),
          );
          this.scriptService.setScripts();
          this.scriptService.load(usedScripts).then(() => {
            this.loadH5PAssets(
              this.assets.css,
              baseUrl,
              this.loadStyleSheet,
              resolve,
            );
          });
        }
      } catch (error) {
        reject(error);
      }
    });

    this.promiseInProgress.then(() => {
      this.promiseInProgress = undefined;
    });

    return this.promiseInProgress;
  }

  initH5PEditorScriptsAndStyles(): Promise<unknown> {
    return new Promise((resolve, reject) => {
      try {
        // @ts-ignore
        const baseUrl = H5PIntegration.editor.baseUrl;
        console.info('Loading editor styles');
        this.loadH5PAssets(
          this.assets.editorCss,
          baseUrl,
          this.loadStyleSheet,
          resolve,
        );
      } catch (error) {
        reject(error);
      }
    });
  }

  initH5PEditor(
    h5pEditorWrapper: HTMLElement,
    loadInIframe = true,
  ): Promise<unknown> {
    return new Promise((resolve, reject) => {
      try {
        // @ts-ignore
        globalThis.isExistingContent = false;

        // @ts-ignore
        globalThis.h5peditor = new globalThis.H5PEditor.Editor(
          undefined,
          undefined,
          h5pEditorWrapper,
          resolve,
          loadInIframe,
        );
      } catch (error) {
        reject(error);
      }
    });
  }

  runActivity(courseActivity: CourseActivity): void {
    const { h5pLibrary, contentJSON } = courseActivity;

    // @ts-ignore
    const loadInIframe = globalThis.H5PEditor.loadInIframe;
    const h5pEditorContentWindow = loadInIframe
      ? this.getH5PEditorContainer().contentWindow
      : globalThis;
    const containerSelector = loadInIframe
      ? 'body > .h5p-editor'
      : '.h5p-editor';

    // @ts-ignore
    const $ = h5pEditorContentWindow.H5P.jQuery;
    const $container = $(containerSelector);

    // @ts-ignore
    globalThis.isExistingContent = true;

    // @ts-ignore
    const libraries = globalThis.h5peditor.selector.libraries;

    // @ts-ignore
    const libraryLoader = new h5pEditorContentWindow.H5PEditor.LibrarySelector(
      libraries,
      h5pLibrary,
      contentJSON,
    );

    // @ts-ignore
    globalThis.h5peditor.selector = libraryLoader;
    libraryLoader.appendTo($container.html(''));

    // Set library if editing
    if (h5pLibrary) {
      libraryLoader.setLibrary(h5pLibrary);
    }
  }

  setupGlobalH5PEditor(): void {
    this.featureFlagService
      .isFeatureFlagEnabled(FEATURES.AI_CONTENT_GENERATION)
      .pipe(take(1))
      .subscribe(aiEnabled => {
        this.companySettingService
          .current()
          .pipe(take(1))
          .subscribe(company => {
            const companyName = company?.name || '';
            this.memberService
              .current()
              .pipe(take(1))
              .subscribe(member => {
                const aiTermsAgreedTo = member?.aiTermsAgreedTo;
                const H5PEditor = {
                  // @ts-ignore
                  $: globalThis.H5P.jQuery,
                  basePath: this.H5PIntegration.editor.baseUrl + '/editor',
                  fileIcon: this.H5PIntegration.editor.fileIcon,
                  ajaxPath: this.H5PIntegration.editor.ajaxPath,
                  filesPath: this.H5PIntegration.editor.filesPath,
                  apiVersion: this.H5PIntegration.editor.apiVersion,
                  contentLanguage: this.languageService.currentLanguage(),
                  // Semantics describing what copyright information can be stored for media.
                  copyrightSemantics:
                    this.H5PIntegration.editor.copyrightSemantics,
                  metadataSemantics:
                    this.H5PIntegration.editor.metadataSemantics,
                  assets: this.H5PIntegration.editor.assets, // Required styles and scripts for editor
                  baseUrl: '', // Required for assets
                  contentId: null,
                  dateTimeConstants: TIME_UNITS,
                  aiEnabled,
                  aiTermsAgreedTo,
                  companyName,
                };

                if (this.H5PIntegration.editor.nodeVersionId !== undefined) {
                  H5PEditor.contentId =
                    this.H5PIntegration.editor.nodeVersionId;
                }

                // @ts-ignore
                if (!globalThis.H5PEditor) {
                  // @ts-ignore
                  globalThis.H5PEditor = {};
                }

                // @ts-ignore
                Object.assign(globalThis.H5PEditor, H5PEditor);
              });
          });
      });

    this.setupGlobalH5PAjaxUrl();
  }

  private setupGlobalH5PAjaxUrl(): void {
    // @ts-ignore
    globalThis.H5PEditor.getAjaxUrl = (action, parameters) => {
      let url = this.H5PIntegration.editor.ajaxPath + action;

      if (parameters !== undefined) {
        for (const property in parameters) {
          if (parameters.hasOwnProperty(property)) {
            url += '&' + property + '=' + parameters[property];
          }
        }
      }

      return url;
    };
  }

  private getH5PEditorContainer(): HTMLIFrameElement {
    // @ts-ignore
    return globalThis.document.querySelector('.h5p-editor-iframe');
  }

  // Use only for initial loading h5p
  private loadH5PAssets(
    assetsArr: Array<string>,
    baseUrl: string,
    executiveFunc: CallableFunction,
    done: CallableFunction,
  ): void {
    let assetCounter = 0;

    assetsArr.forEach(asset => {
      assetCounter++;
      // @ts-ignore
      executiveFunc.call(this, baseUrl + '/' + asset, assetCounter, done);
    });
  }

  private loadStyleSheet(
    src: string,
    styleCounter: number,
    done: CallableFunction,
  ): void {
    // @ts-ignore
    globalThis.ns
      .$('head')
      .append(`<link rel="stylesheet" href="${src}" type="text/css" />`);
    styleCounter++;
    if (styleCounter === this.assets.css.length) {
      done();
    }
  }
}
